import { memo, useCallback, useEffect, useRef, useState, ChangeEvent, DragEvent } from 'react';
import classNames from 'classnames';
import { V2QsImageProps } from './types';

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * V2QsImage 컴포넌트
 *
 * 이미지 업로드를 위한 컴포넌트
 * 드래그 앤 드롭 및 클릭 업로드 지원
 *
 * @example
 * ```tsx
 * <V2QsImage
 *   value={imageUrl}
 *   onFileSelect={handleFileSelect}
 *   onChange={setImageUrl}
 *   onRemove={handleRemove}
 * />
 * ```
 */
function V2QsImage({
  value,
  onFileSelect,
  onChange,
  onRemove,
  isUploading = false,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  dropzoneText = 'Drag and drop or click to upload',
  fileTypeHint = 'JPEG, PNG, GIF, WebP',
  uploadingText = 'Uploading...',
  removeButtonText = 'Remove',
  showRemoveButton = true,
  previewMaxHeight = 200,
}: V2QsImageProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(value || null);

  // 외부 value 변경 시 동기화
  useEffect(() => {
    setPreviewUrl(value || null);
  }, [value]);

  // 파일 유효성 검증
  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxFileSize) {
        const maxMB = Math.round(maxFileSize / (1024 * 1024));
        return `File size exceeds ${maxMB}MB limit`;
      }

      if (!acceptedTypes.includes(file.type)) {
        return 'Invalid file type';
      }

      return null;
    },
    [maxFileSize, acceptedTypes]
  );

  // 파일 처리
  const handleFile = useCallback(
    (file: File) => {
      setLocalError(null);

      const validationError = validateFile(file);
      if (validationError) {
        setLocalError(validationError);
        return;
      }

      // 미리보기 생성
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setPreviewUrl(result);
        onChange?.(result);
      };
      reader.readAsDataURL(file);

      // 파일 선택 핸들러 호출
      onFileSelect?.(file);
    },
    [validateFile, onChange, onFileSelect]
  );

  // 파일 입력 핸들러
  const handleFileInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
      // 같은 파일 재선택 허용을 위해 input 값 리셋
      e.target.value = '';
    },
    [handleFile]
  );

  // 드래그 이벤트 핸들러
  const handleDrag = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled || readOnly || isUploading) return;

      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true);
      } else if (e.type === 'dragleave') {
        setDragActive(false);
      }
    },
    [disabled, readOnly, isUploading]
  );

  // 드롭 핸들러
  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (disabled || readOnly || isUploading) return;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFile(e.dataTransfer.files[0]);
      }
    },
    [disabled, readOnly, isUploading, handleFile]
  );

  // 클릭 핸들러
  const handleClick = useCallback(() => {
    if (disabled || readOnly || isUploading) return;
    fileInputRef.current?.click();
  }, [disabled, readOnly, isUploading]);

  // 삭제 핸들러
  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setPreviewUrl(null);
      setLocalError(null);
      onChange?.('');
      onRemove?.();
    },
    [onChange, onRemove]
  );

  const hasError = error || !!localError;
  const displayError = errorMessage || localError;
  const hasImage = !!previewUrl;
  const acceptString = acceptedTypes.join(',');

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // Dropzone 클래스
  const dropzoneClasses = classNames(
    'relative border-2 border-dashed rounded-[10px] transition-all duration-200 cursor-pointer',
    {
      // Width
      'w-full': fullWidth,

      // Active states
      'border-[#4B7BF5] bg-[#F0F4FF]': dragActive && !disabled,
      'border-[#E6E9F5] hover:border-[#4B7BF5] hover:bg-[#F9FAFB]':
        !dragActive && !disabled && !readOnly && !hasError,
      'border-[#FF5E71] bg-[#FFF5F6]': hasError && !disabled,

      // Disabled state
      'border-[#E6E9F5] bg-[#F2F4FC] cursor-not-allowed opacity-60': disabled,

      // ReadOnly state
      'cursor-default': readOnly,
    },
    className
  );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Dropzone */}
      <div
        className={dropzoneClasses}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={!hasImage ? handleClick : undefined}
      >
        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={acceptString}
          onChange={handleFileInput}
          disabled={disabled || isUploading || readOnly}
        />

        {/* Content */}
        {hasImage ? (
          // 이미지 미리보기
          <div className="relative p-4">
            <img
              src={previewUrl}
              alt="Uploaded"
              className="mx-auto object-contain rounded-lg"
              style={{ maxHeight: previewMaxHeight }}
            />

            {/* 삭제 버튼 */}
            {showRemoveButton && !readOnly && !disabled && (
              <button
                type="button"
                className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-[#F2F4FC] transition-colors"
                onClick={handleRemove}
                title={removeButtonText}
              >
                <svg
                  className="w-5 h-5 text-[#FF5E71]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}

            {/* 변경 버튼 */}
            {!readOnly && !disabled && (
              <button
                type="button"
                className="absolute bottom-2 right-2 px-3 py-1 bg-white rounded-lg shadow-md text-sm text-[#222638] hover:bg-[#F2F4FC] transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
              >
                Change
              </button>
            )}
          </div>
        ) : (
          // 업로드 영역
          <div className="flex flex-col items-center justify-center p-8 gap-3">
            {isUploading ? (
              // 업로드 중
              <>
                <div className="w-12 h-12 rounded-full bg-[#4B7BF5] bg-opacity-10 flex items-center justify-center">
                  <span className="loading loading-spinner loading-md text-[#4B7BF5]" />
                </div>
                <p className="text-sm font-medium text-[#222638]">{uploadingText}</p>
              </>
            ) : (
              // 대기 상태
              <>
                <div className="w-12 h-12 rounded-full bg-[#4B7BF5] bg-opacity-10 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-[#4B7BF5]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-[#222638] mb-1">{dropzoneText}</p>
                  <p className="text-xs text-[#AFB2C2]">{fileTypeHint}</p>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && displayError && <span className={errorClasses}>{displayError}</span>}
    </div>
  );
}

export default memo(V2QsImage);
